import { TrackingEventKey } from "@next-platform/packages-ts-types";

/** Conversion payload the checkout-success URL can populate. Structurally a `FireConversionParams`. */
export type ConversionInputs = {
  eventKey: TrackingEventKey;
  eventId: string;
  value: number;
  currency: string;
  transactionId?: string;
  subscriptionId?: string;
  paymentSystem?: string;
  productId?: string;
  customerId?: string;
  clientUserId?: string;
};

/**
 * Observed on a sandbox card order: Sticky sent this literal string for
 * `transactionID` instead of omitting the field. Likely test-account specific,
 * but forwarding it would be worse than sending nothing — every such order
 * would share one "identifier", colliding across unrelated sales.
 *
 * Applied to every field, `orderId` included: an order id reading
 * "Not Available" would become Meta's dedup key and merge unrelated
 * conversions, so suppressing the event entirely is the lesser harm.
 */
const STICKY_PLACEHOLDER_VALUE = "Not Available";

/** Absent, blank, and Sticky's placeholder all mean "no value". */
function emptyToUndefined(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === "" || value === STICKY_PLACEHOLDER_VALUE) {
    return undefined;
  }
  return value;
}

/**
 * Build the browser conversion payload from the checkout-success URL, or
 * `null` when the URL carries too little to report a conversion.
 *
 * `null` rather than a partial event when either the order id or a parseable
 * amount is missing: the order id is the dedup key shared with the backend
 * CAPI producer, and every event built here is a revenue event.
 *
 * Note this is why the alternative payment processors report nothing today —
 * they return to `/checkout/success` via the server-built URL, which carries
 * only `orderTotal`, `date`, `plan` and `currency`.
 */
export default function buildCheckoutConversionInputs(params: {
  orderId: string | null;
  transactionId: string | null;
  orderTotal: string | undefined;
  netAmount: string | undefined;
  subscriptionId: string | null;
  currency: string;
  paymentSystem: string | undefined;
  productId: string | undefined;
  customerId: string | undefined;
  clientUserId: string | null;
}): ConversionInputs | null {
  const { orderId, transactionId, orderTotal, netAmount, subscriptionId, currency, paymentSystem, productId, customerId, clientUserId } = params;
  const resolvedOrderId = emptyToUndefined(orderId);
  if (resolvedOrderId === undefined) {
    return null;
  }
  // Tax-exclusive net when the checkout form could compute one; `orderTotal`
  // (gross) equals net in flows without tax. This value feeds Meta's
  // conversion value as well, so it is not CampaignsWell's to redefine —
  // they'd prefer the gross main-period list price for `price`, but confirmed
  // the field is optional, and switching would move Meta's reported revenue.
  const rawAmount = netAmount !== undefined && netAmount !== "" ? netAmount : orderTotal;
  if (rawAmount === undefined || rawAmount === "") {
    return null;
  }
  const value = Number.parseFloat(rawAmount);
  if (!Number.isFinite(value)) {
    return null;
  }
  // Renewals never reach here — they fire from the billing webhook.
  //
  // Known limitation: Sticky also assigns a `subscription_id` to non-recurring
  // purchases (CampaignsWell confirmed this from their Sticky data), so this
  // discriminator would label an instant/coin purchase as `Subscribe`. Safe
  // today — our web checkout only sells subscriptions, and there is no
  // `instant` notion client-side — but it needs a real signal (billing
  // frequency) the day one-off products reach this page.
  const resolvedSubscriptionId = emptyToUndefined(subscriptionId);
  const eventKey = resolvedSubscriptionId !== undefined ? TrackingEventKey.Subscribe : TrackingEventKey.Purchase;
  const resolvedTransactionId = emptyToUndefined(transactionId);
  const resolvedPaymentSystem = emptyToUndefined(paymentSystem);
  const resolvedProductId = emptyToUndefined(productId);
  const resolvedCustomerId = emptyToUndefined(customerId);
  const resolvedClientUserId = emptyToUndefined(clientUserId);
  return {
    eventKey,
    // The dedup key is the Sticky **order** id, not the transaction id.
    // Mind the naming trap on the server: `StickySubscriptionService` maps
    // `transaction_id: params.orderId`, and `subscriptionService` then passes
    // that variable as `eventId` — so the backend's CAPI `event_id` is the
    // order id despite the field name. These two MUST match or Meta counts
    // the pixel hit and the CAPI hit as two separate conversions.
    eventId: resolvedOrderId,
    value,
    currency,
    // Deliberately NOT the same value as `eventId`: CampaignsWell asked for
    // the payment/transaction id here, which is what the server-side CATTRNX
    // provider also sends (`processorTransactionId ?? orderId`). Do not
    // collapse these two fields into one.
    ...(resolvedTransactionId !== undefined ? { transactionId: resolvedTransactionId } : {}),
    ...(resolvedSubscriptionId !== undefined ? { subscriptionId: resolvedSubscriptionId } : {}),
    ...(resolvedPaymentSystem !== undefined ? { paymentSystem: resolvedPaymentSystem } : {}),
    ...(resolvedProductId !== undefined ? { productId: resolvedProductId } : {}),
    // Sticky's own customer id, and our internal user id. Two different
    // namespaces: the first joins to Sticky billing data, the second to the
    // `user_id` on our CATTRNX postbacks.
    ...(resolvedCustomerId !== undefined ? { customerId: resolvedCustomerId } : {}),
    ...(resolvedClientUserId !== undefined ? { clientUserId: resolvedClientUserId } : {}),
  };
}
